import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Chip,
  Link,
  Grid,
  IconButton,
  Stack,
  Divider,
  AppBar,
  Toolbar,
  Collapse
} from '@mui/material';
import {
  Check as CheckIcon,
  Search as SearchIcon,
  CloudUpload as CloudUploadIcon,
  PhotoCamera as PhotoCameraIcon,
  Menu as MenuIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  YouTube as YouTubeIcon,
  Pinterest as PinterestIcon,
  LinkedIn as LinkedInIcon,
  Phone,
  Rocket as RocketIcon,
  Business as BusinessIcon,
  WorkspacePremium as PremiumIcon,
  Compare as CompareIcon,
  Star as StarIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Support as SupportIcon,
  Chat as ChatIcon,
  Analytics as AnalyticsIcon,
  ArrowForward as ArrowForwardIcon,
  Diamond as DiamondIcon,
  Bolt as BoltIcon,
  QuestionAnswer as QuestionAnswerIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTheme, useMediaQuery } from '@mui/material';
import { TextField } from '@mui/material';
import logo from '../assets/logo0.png';
import pricinghero from '../assets/pricinghero.png';
import background from '../assets/background.png';
import menuIcon from '../assets/menu.png';

const Logo = styled('img')({
  height: '30px',
  marginRight: '1rem',
  filter: 'brightness(0) invert(1)',
});

const commonButtonStyles = {
  textTransform: 'none',
  borderRadius: '8px',
  transition: 'all 0.3s ease',
  fontWeight: 600,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  }
};

const TopHeader = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ bgcolor: '#333', color: 'white', py: 1 }}>
      <Container maxWidth="xl">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 1 : 0
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 3 
          }}>
            <Link href="tel:089998288026" sx={{ color: 'white', textDecoration: 'none' }}>
              089 998 288 026
            </Link>
            {!isMobile && <Link href="/support" sx={{ color: 'white', textDecoration: 'none' }}>Support</Link>}
          </Box>
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            flexDirection: isMobile ? 'column' : 'row',
            width: isMobile ? '100%' : 'auto'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label="Neu" sx={{ bgcolor: '#9ACD32', color: '#000', height: '24px' }} />
              <Link href="/refer-friends" sx={{ color: 'white', textDecoration: 'none' }}>Freunde werben</Link>
            </Box>
            <Link href="/login" sx={{ color: 'white', textDecoration: 'none' }}>Login</Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const MainNav = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const menuItems = [
    'Smartphones',
    'Computers & Laptops',
    'Home Appliances',
    'Gaming Consoles',
    'Cloud Services',
    'IoT Devices'
  ];

  return (
    <Box sx={{ bgcolor: '#1a1a1a', py: 2 }}>
      <Container maxWidth="xl">
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          position: 'relative'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Logo src={logo} alt="Larvaries" />
          </Box>
          
          {isMobile ? (
            <>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)'
              }}>
                <Button
                  variant="contained"
                  startIcon={<QuestionAnswerIcon />}
                  href="/login?redirect=/ask-question"
                  sx={{
                    ...commonButtonStyles,
                    bgcolor: '#00693C',
                    color: 'white',
                    px: 3,
                    py: 1.2,
                    fontSize: '0.95rem',
                    '&:hover': {
                      ...commonButtonStyles['&:hover'],
                      bgcolor: '#005732',
                    }
                  }}
                >
                  Ask Question
                </Button>
                <IconButton 
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  sx={{ 
                    color: 'white',
                    padding: '8px',
                    marginLeft: '8px'
                  }}
                >
                  <MenuIcon sx={{ fontSize: '24px' }} />
                </IconButton>
              </Box>
              
              <Collapse in={mobileMenuOpen} sx={{ 
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1000
              }}>
                <Box sx={{ 
                  bgcolor: '#000000',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}>
                  {menuItems.map((item, index) => (
                    <Link 
                      key={item}
                      href={`#${item.toLowerCase().replace(/\s+/g, '-')}`}
                      sx={{ 
                        color: 'white',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                        padding: '12px',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.1)',
                        }
                      }}
                    >
                      {item}
                    </Link>
                  ))}
                </Box>
              </Collapse>
            </>
          ) : (
            <Box sx={{ 
              display: 'flex',
              gap: 4,
              alignItems: 'center'
            }}>
              {menuItems.map((item, index) => (
                <Link 
                  key={item}
                  href={`#${item.toLowerCase().replace(/\s+/g, '-')}`}
                  sx={{ 
                    color: 'white',
                    textDecoration: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                    opacity: 0.9,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      opacity: 1,
                      transform: 'translateY(-1px)',
                      color: '#FFD700'
                    }
                  }}
                >
                  {item}
                </Link>
              ))}
              <Button
                variant="contained"
                startIcon={<QuestionAnswerIcon />}
                href="/login?redirect=/ask-question"
                sx={{
                  ...commonButtonStyles,
                  bgcolor: '#00693C',
                  color: 'white',
                  px: 3,
                  py: 1.2,
                  fontSize: '0.95rem',
                  '&:hover': {
                    ...commonButtonStyles['&:hover'],
                    bgcolor: '#005732',
                  }
                }}
              >
                Ask Question
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

const SearchSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ bgcolor: '#00693C', py: 4 }}>
      <Container maxWidth="xl">
        <Box sx={{ 
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: 3
        }}>
          <Box sx={{ 
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            flex: 1
          }}>
            <TextField 
              fullWidth
              placeholder="Search or upload file"
              sx={{ 
                bgcolor: 'white',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  height: '48px'
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="contained" 
                startIcon={<CloudUploadIcon />}
                sx={{
                  ...commonButtonStyles,
                  bgcolor: 'white',
                  color: '#00693C',
                  px: 3,
                  '&:hover': {
                    ...commonButtonStyles['&:hover'],
                    bgcolor: '#f8f8f8',
                  }
                }}
              >
                Upload
              </Button>
              <Button 
                variant="contained"
                startIcon={<SearchIcon />}
                sx={{
                  ...commonButtonStyles,
                  bgcolor: 'white',
                  color: '#00693C',
                  px: 3,
                  '&:hover': {
                    ...commonButtonStyles['&:hover'],
                    bgcolor: '#f8f8f8',
                  }
                }}
              >
                Scan
              </Button>
            </Box>
          </Box>

          <Box sx={{ 
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            gap: 2,
            textAlign: isMobile ? 'center' : 'left'
          }}>
            <Typography variant="h5" sx={{ color: 'white', whiteSpace: 'nowrap' }}>
              0,08 €* / pro Monat
            </Typography>
            <Box sx={{ 
              display: 'flex',
              gap: 1,
              justifyContent: isMobile ? 'center' : 'flex-start'
            }}>
              <Chip label=".de" sx={{ bgcolor: 'white' }} />
              <Chip label=".com" sx={{ bgcolor: 'white' }} />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const Footer = () => {
  const socialButtonStyles = {
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-3px) rotate(8deg)',
      color: '#FFD700',
    }
  };

  return (
    <Box sx={{ bgcolor: '#333', color: 'white', py: 6 }}>
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          <Grid item xs={12} sx={{ mb: 4 }}>
            <Logo src={logo} alt="Domain Factory" />
          </Grid>
          {/* Products Column */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" sx={{ mb: 2 }}>Produkte</Typography>
            <Stack spacing={1}>
              <Link href="#domains" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Domains</Link>
              <Link href="#homepage" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Homepage</Link>
              <Link href="#webhosting" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Webhosting</Link>
              <Link href="#webhosting-plus" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Webhosting Plus</Link>
              <Link href="#email" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>E-Mail Essentials</Link>
              <Link href="#managed-server" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Managed Cloud Server</Link>
              <Link href="#ssl" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>SSL-Zertifikate</Link>
              <Link href="#preise" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Preise</Link>
            </Stack>
          </Grid>

          {/* Company Column */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" sx={{ mb: 2 }}>Unternehmen</Typography>
            <Stack spacing={1}>
              <Link href="#uber-uns" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Über uns</Link>
              <Link href="#jobs" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Jobs</Link>
              <Link href="#referenzen" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Referenzen</Link>
              <Link href="#service" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Service</Link>
              <Link href="#partnerprogramm" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Partnerprogramm</Link>
              <Link href="#freunde-werben" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Freunde werben</Link>
            </Stack>
          </Grid>

          {/* Legal Column */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" sx={{ mb: 2 }}>Rechtliches</Typography>
            <Stack spacing={1}>
              <Link href="#impressum" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Impressum</Link>
              <Link href="#datenschutz" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Datenschutz</Link>
              <Link href="#cookies" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Cookies</Link>
              <Link href="#widerrufsrecht" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Widerrufsrecht</Link>
              <Link href="#kundigen" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Jetzt kündigen</Link>
              <Link href="#agb" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>AGB</Link>
              <Link href="#digital-services" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Digital Services Act</Link>
              <Link href="#offenlegung" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Offenlegung</Link>
            </Stack>
          </Grid>

          {/* Support Column */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" sx={{ mb: 2 }}>Support</Typography>
            <Stack spacing={1}>
              <Link href="#formulare" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Formulare</Link>
              <Link href="#faq" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>FAQ</Link>
              <Link href="#statusmeldung" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Statusmeldung</Link>
              <Link href="#downloads" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Downloads</Link>
              <Link href="#kontakt" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Kontakt</Link>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>089 998 288 026</Typography>
                <Typography variant="body2">support@df.eu</Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        {/* Social Media & Blog Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mt: 4,
          pt: 4,
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Link href="#blog" sx={{ color: 'white', textDecoration: 'none' }}>
            <Typography>Blog</Typography>
          </Link>
          <Stack direction="row" spacing={2}>
            <IconButton color="inherit" sx={socialButtonStyles}>
              <FacebookIcon />
            </IconButton>
            <IconButton color="inherit" sx={socialButtonStyles}>
              <TwitterIcon />
            </IconButton>
            <IconButton color="inherit" sx={socialButtonStyles}>
              <YouTubeIcon />
            </IconButton>
            <IconButton color="inherit" sx={socialButtonStyles}>
              <PinterestIcon />
            </IconButton>
            <IconButton color="inherit" sx={socialButtonStyles}>
              <LinkedInIcon />
            </IconButton>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

const PricingSection = () => {
  return (
    <Box sx={{ 
      bgcolor: '#000000',
      py: 6,
      backgroundImage: 'url(../assets/background.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        zIndex: 1
      }
    }}>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Top Navigation Links */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          mb: 4 
        }}>
          <Link href="#" sx={{ 
            color: '#ffffff',
            textDecoration: 'none',
            fontWeight: 500,
            '&:hover': { color: '#00693C' }
          }}>
            AI Services
          </Link>
          <Typography color="white">•</Typography>
          <Link href="#" sx={{ 
            color: '#ffffff',
            textDecoration: 'none',
            fontWeight: 500,
            '&:hover': { color: '#00693C' }
          }}>
            Diagnostics
          </Link>
          <Typography color="white">•</Typography>
          <Link href="#" sx={{ 
            color: '#ffffff',
            textDecoration: 'none',
            fontWeight: 500,
            '&:hover': { color: '#00693C' }
          }}>
            Support
          </Link>
        </Box>

        {/* Main Content Grid */}
        <Grid container spacing={4}>
          {/* Left Content */}
          <Grid item xs={12} md={7}>
            <Typography variant="h2" sx={{ 
              fontWeight: 800,
              fontSize: { xs: '2.5rem', md: '3.2rem' },
              mb: 3,
              color: '#ffffff',
              lineHeight: 1.2,
              fontFamily: 'Inter, sans-serif'
            }}>
              🚀 Standard Plan - 
              <br />
              Smart and Secure.
            </Typography>

            {/* Promotional Banner */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              mb: 4,
              flexWrap: 'wrap'
            }}>
              <Chip 
                label="NEW" 
                sx={{ 
                  bgcolor: '#9ACD32',
                  color: '#000',
                  fontWeight: 'bold',
                  height: '24px'
                }}
              />
              <Typography color="white">
                Subscribe now and save: 
                <span style={{ fontWeight: 'bold' }}> Get an additional 10% discount </span>
                with a 2-year subscription.
              </Typography>
            </Box>

            {/* Feature box */}
            <Box sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              p: 3,
              borderRadius: 2,
              mb: 4,
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckIcon sx={{ color: '#00693C' }} />
                  <Typography color="white">.de Domain 1 Year included</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckIcon sx={{ color: '#00693C' }} />
                  <Typography color="white">Ultra-fast SSD Storage</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckIcon sx={{ color: '#00693C' }} />
                  <Typography color="white">Security with SSL Certificate</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckIcon sx={{ color: '#00693C' }} />
                  <Typography color="white">Expert Support</Typography>
                </Box>
              </Stack>
            </Box>

            {/* Pricing Display */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  mb: 2,
                  color: '#666'
                }}
              >
                Standard Package
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'baseline', 
                gap: 1,
                mb: 3
              }}>
                <Typography 
                  component="span" 
                  sx={{ 
                    fontSize: '0.9rem',
                    color: '#666'
                  }}
                >
                  From
                </Typography>
                <Typography 
                  variant="h3" 
                  component="span" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: '#00693C'
                  }}
                >
                  1,500 DZD
                </Typography>
                <Typography 
                  component="span" 
                  sx={{ 
                    fontSize: '0.9rem',
                    color: '#666'
                  }}
                >
                  /month*
                </Typography>
                <Chip 
                  label="-10% 1,650 DZD" 
                  sx={{ 
                    bgcolor: '#e8f5e9',
                    color: '#00693C',
                    ml: 2
                  }}
                />
              </Box>

              <Button
                variant="contained"
                startIcon={<RocketIcon />}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  ...commonButtonStyles,
                  bgcolor: '#00693C',
                  color: 'white',
                  px: 4,
                  py: 1.8,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  '&:hover': {
                    ...commonButtonStyles['&:hover'],
                    bgcolor: '#005732',
                  }
                }}
              >
                Get Started
              </Button>
            </Box>

            {/* Feature List */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {[
                'Unlimited AI diagnostics for all devices',
                'Advanced troubleshooting guides (software & hardware)',
                'Cloud storage (10GB)',
                'Priority support via chat',
                'Regular performance optimization tips'
              ].map((feature, index) => (
                <Grid item xs={12} key={index}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    py: 1
                  }}>
                    <CheckIcon sx={{ 
                      color: '#00693C',
                      fontSize: '1.2rem'
                    }} />
                    <Typography sx={{ 
                      fontSize: '1rem',
                      color: '#333'
                    }}>
                      {feature}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Right Image */}
          <Grid item xs={12} md={5}>
            <Box
              component="img"
              src={pricinghero}
              alt="AI Diagnostics"
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: '16px',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
                }
              }}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

const AdditionalPricingCards = () => {
  const plans = [
    {
      label: 'Popular',
      title: 'Basic Plan',
      icon: <RocketIcon sx={{ fontSize: 40, color: '#00693C' }} />,
      price: '1,500',
      period: '/Month*',
      laterPrice: 'später 1,800 DZD/Month',
      buttonText: 'Compare Plans',
      description: 'Perfect for small businesses and startups looking for essential AI diagnostics.',
      features: [
        { text: 'Basic AI diagnostics', icon: <SpeedIcon /> },
        { text: 'Standard support', icon: <SupportIcon /> },
        { text: '5GB Cloud storage', icon: <CloudUploadIcon /> },
        { text: 'Chat assistance', icon: <ChatIcon /> }
      ]
    },
    {
      label: 'Best Value',
      title: 'Professional',
      icon: <BusinessIcon sx={{ fontSize: 40, color: '#00693C' }} />,
      price: '2,500',
      period: '/Month*',
      laterPrice: 'später 2,900 DZD/Month',
      buttonText: 'Compare Plans',
      description: 'Advanced AI diagnostics with priority support and extended features.',
      features: [
        { text: 'Advanced AI analytics', icon: <AnalyticsIcon /> },
        { text: 'Priority support 24/7', icon: <StarIcon /> },
        { text: '20GB Cloud storage', icon: <CloudUploadIcon /> },
        { text: 'Video consultations', icon: <ChatIcon /> }
      ],
      highlighted: true
    },
    {
      title: 'Enterprise',
      icon: <DiamondIcon sx={{ fontSize: 40, color: '#00693C' }} />,
      price: '4,500',
      period: '/Month*',
      prefix: 'from',
      buttonText: 'Compare Plans',
      description: 'Complete AI diagnostic solution with unlimited access and dedicated support.',
      features: [
        { text: 'Custom AI solutions', icon: <BoltIcon /> },
        { text: 'Dedicated support team', icon: <SupportIcon /> },
        { text: 'Unlimited storage', icon: <CloudUploadIcon /> },
        { text: 'API access', icon: <SecurityIcon /> }
      ]
    }
  ];

  const getButtonStyles = (isHighlighted) => ({
    ...commonButtonStyles,
    width: '100%',
    py: 1.8,
    fontSize: '1rem',
    fontWeight: 600,
    position: 'relative',
    overflow: 'hidden',
    bgcolor: isHighlighted ? '#FFD700' : '#00693C',
    color: isHighlighted ? '#000' : 'white',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: `linear-gradient(
        90deg,
        transparent,
        rgba(255,255,255,0.2),
        transparent
      )`,
      transition: 'left 0.7s ease',
    },
    '&:hover': {
      ...commonButtonStyles['&:hover'],
      bgcolor: isHighlighted ? '#FFE44D' : '#005732',
      '&::before': {
        left: '100%',
      }
    }
  });

  return (
    <Box sx={{ 
      bgcolor: '#FFD700', 
      py: 8,
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '100%',
        background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0) 100%)'
      }
    }}>
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            mb: 2,
            fontWeight: 800,
            color: '#333',
            position: 'relative'
          }}
        >
          AI Diagnostic Plans
        </Typography>
        
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            mb: 6,
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Choose the perfect plan for your needs with our flexible pricing options
        </Typography>
        
        <Grid container spacing={4}>
          {plans.map((plan, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box
                sx={{
                  bgcolor: plan.highlighted ? '#00693C' : 'white',
                  p: 4,
                  borderRadius: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  boxShadow: plan.highlighted 
                    ? '0 8px 32px rgba(0,105,60,0.25)' 
                    : '0 2px 12px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  transform: plan.highlighted ? 'scale(1.05)' : 'none',
                  '&:hover': {
                    transform: plan.highlighted ? 'scale(1.07)' : 'translateY(-8px)',
                    boxShadow: plan.highlighted 
                      ? '0 12px 40px rgba(0,105,60,0.3)' 
                      : '0 8px 28px rgba(0,0,0,0.15)'
                  }
                }}
              >
                {plan.label && (
                  <Chip
                    label={plan.label}
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      bgcolor: plan.highlighted ? '#FFD700' : '#9ACD32',
                      color: '#000',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      px: 2
                    }}
                  />
                )}

                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                  {plan.icon}
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: plan.highlighted ? 'white' : '#333'
                    }}
                  >
                    {plan.title}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  {plan.prefix && (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '0.9rem',
                        color: plan.highlighted ? 'rgba(255,255,255,0.7)' : '#666'
                      }}
                    >
                      {plan.prefix}
                    </Typography>
                  )}
                  <Typography
                    variant="h3"
                    component="span"
                    sx={{
                      fontWeight: 800,
                      color: plan.highlighted ? 'white' : '#333'
                    }}
                  >
                    {plan.price} DZD
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.9rem',
                      color: plan.highlighted ? 'rgba(255,255,255,0.7)' : '#666'
                    }}
                  >
                    {plan.period}
                  </Typography>
                </Box>

                {plan.laterPrice && (
                  <Typography
                    sx={{
                      fontSize: '0.85rem',
                      color: plan.highlighted ? 'rgba(255,255,255,0.7)' : '#666',
                      mb: 3
                    }}
                  >
                    {plan.laterPrice}
                  </Typography>
                )}

                <Box sx={{ mb: 4 }}>
                  {plan.features.map((feature, idx) => (
                    <Box 
                      key={idx} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2,
                        mb: 2
                      }}
                    >
                      <Box sx={{ 
                        color: plan.highlighted ? '#FFD700' : '#00693C',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        {feature.icon}
                      </Box>
                      <Typography
                        sx={{
                          color: plan.highlighted ? 'white' : '#666'
                        }}
                      >
                        {feature.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Button
                  variant="contained"
                  startIcon={<CompareIcon />}
                  endIcon={<ArrowForwardIcon />}
                  sx={getButtonStyles(plan.highlighted)}
                >
                  {plan.buttonText}
                </Button>

                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    color: plan.highlighted ? 'rgba(255,255,255,0.8)' : '#666',
                    textAlign: 'center',
                    mt: 3
                  }}
                >
                  {plan.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const Pricing = () => {
  return (
    <Box>
      <TopHeader />
      <MainNav />
      <PricingSection />
      <Divider sx={{ my: 4 }} />
      <AdditionalPricingCards />
      <Footer />
    </Box>
  );
};

export default Pricing; 